import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'fr';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// English translations
const enTranslations: Record<string, string> = {
  // Hero section
  'hero.title': 'Yasser Kouddan',
  'hero.subtitle.fullstack': 'Full Stack Web Developer',
  'hero.subtitle.webdev': 'Full Stack Mobile Developer',
  'hero.subtitle.ai': 'Aspiring AI Researcher',
  'hero.description': 'Passionate and versatile developer with over 2 years in the React Native and React ecosystem. Designing and implementing innovative solutions in a collaborative international environment.',
  'hero.viewExperience': 'View Experience',
  'hero.seeProjects': 'See Projects',
  
  // Experience section
  'experience.title': 'Professional Experience',
  'experience.bi.title': 'Business Intelligence Intern',
  'experience.bi.company': 'LPEE',
  'experience.bi.period': 'February - July 2024',
  'experience.bi.location': 'Morocco',
  'experience.bi.description.1': 'Analyzed business requirements and identified key indicators to be monitored',
  'experience.bi.description.2': 'Cleaned up and transformed Excel data using Power Query and Python',
  'experience.bi.description.3': 'Created interactive dashboards in Power BI to visualise budget variances',
  'experience.bi.description.4': 'Automated report updates, reducing processing time from hours to minutes',
  
  'experience.pickone.title': 'FullStack Mobile Developer',
  'experience.pickone.company': 'Startup "PickOne"',
  'experience.pickone.period': 'April 2023 - ongoing',
  'experience.pickone.location': 'France',
  'experience.pickone.description.1': 'Developed an innovative poll-based mobile social network application using React Native (Expo) and Express.js',
  'experience.pickone.description.2': 'Designed user interface using Figma for intuitive and consistent mobile experience',
  'experience.pickone.description.3': 'Developed mobile interface using React Native, Expo and TypeScript',
  'experience.pickone.description.4': 'Integrated RESTful APIs via Axios for authentication, voting and user management',
  'experience.pickone.description.5': 'Implemented advanced features like deep linking, content sharing, and theme management',
  
  'experience.freelance.title': 'Mobile Developer',
  'experience.freelance.company': 'Freelance',
  'experience.freelance.period': 'September - February 2023',
  'experience.freelance.location': 'Morocco',
  'experience.freelance.description.1': 'Developed a full stack mobile application using React Native, Expo and Firebase',
  'experience.freelance.description.2': 'Gathered and analyzed client expectations to define project requirements',
  'experience.freelance.description.3': 'Designed intuitive user interfaces using Figma',
  'experience.freelance.description.4': 'Developed frontend and backend with reusable components and Firebase integration',
  
  // Projects section
  'projects.title': 'Projects',
  'projects.recommendation.title': 'Recommendation System for E-learning Platforms',
  'projects.recommendation.description': 'Final year project implementing a sophisticated recommendation system using machine learning algorithms to enhance the learning experience.',
  'projects.moncomptable.title': 'MonComptable',
  'projects.moncomptable.description': 'Mobile application for supermarket stock management, featuring real-time inventory tracking and analytics.',
  'projects.stadioapp.title': 'StadioApp',
  'projects.stadioapp.description': 'Mobile application for football field rental management, featuring real-time booking and management system.',
  'projects.university.title': 'University Management System',
  'projects.university.description': 'Web application for university management, featuring student and course management system.',
  
  // Skills section
  'skills.title': 'Skills',
  'skills.languages': 'Languages',
  'skills.french': 'French',
  'skills.english': 'English',
  'skills.arabic': 'Arabic',
  'skills.fluent': 'Fluent',
  'skills.professional': 'Professional',
  'skills.native': 'Native',
  'skills.categories.frameworks': 'Frameworks',
  'skills.categories.libraries': 'Libraries',
  'skills.categories.programming': 'Programming Languages',
  'skills.categories.theoretical': 'Theoretical Knowledge',
  'skills.categories.environments': 'Environments and IDEs',
  'skills.categories.office': 'Office Skills',
  
  // Education section
  'education.title': 'Education',
  'education.mst.degree': 'Master of Science and Technology (MST)',
  'education.mst.school': 'Faculty of Science and Technology - Sultan Moulay Slimane University',
  'education.mst.period': '2022 - ongoing',
  'education.mst.field': 'Artificial Intelligence & Digital Computing (AIDC)',
  'education.mst.status': 'Expected graduation July 2025',
  
  'education.lst.degree': 'Bachelor of Science and Technology (LST)',
  'education.lst.school': 'Faculty of Science and Technology - Sultan Moulay Slimane University',
  'education.lst.period': '2021 - 2022',
  'education.lst.field': 'Computer science and software engineering',
  
  // Interests section
  'interests.title': 'Interests',
  'interests.reading.name': 'Reading and Writing Fantasy Novels',
  'interests.reading.description': 'Exploring imaginative worlds and creating new stories',
  'interests.manga.name': 'Anime and Manga',
  'interests.manga.description': 'Enjoying Japanese comics / animes and their unique storytelling',
  'interests.science.name': 'Science and Technology',
  'interests.science.description': 'Exploring scientific discoveries and research especially in the field of artificial intelligence',
  'interests.kickboxing.name': 'KickBoxing',
  'interests.kickboxing.description': 'Training in martial arts for fitness and discipline',
  'interests.swimming.name': 'Swimming',
  'interests.swimming.description': 'Staying active and enjoying water sports',
  'interests.gaming.name': 'Video Games (RPG)',
  'interests.gaming.description': 'Exploring virtual worlds and role playing games',
  
  // Contact section
  'contact.title': 'Contact Me',
  'contact.getInTouch': 'Get In Touch',
  'contact.name': 'Name',
  'contact.email': 'Email',
  'contact.message': 'Message',
  'contact.sendMessage': 'Send Message',
  'contact.sending': 'Sending...',
  'contact.success': 'Your message has been sent successfully!',
  'contact.successSubtext': 'I\'ll get back to you as soon as possible.',
  'contact.error': 'An error occurred. Please try again later.',
  'contact.failed': 'Failed to send message. Please try again.',
};

// French translations
const frTranslations: Record<string, string> = {
  // Hero section
  'hero.title': 'Yasser Kouddan',
  'hero.subtitle.fullstack': 'Développeur Web Full Stack',
  'hero.subtitle.webdev': 'Développeur Mobile Full Stack',
  'hero.subtitle.ai': 'Chercheur en IA en devenir',
  'hero.description': 'Développeur passionné et polyvalent avec plus de 2 ans d\'expérience dans l\'écosystème React Native et React. Conception et mise en œuvre de solutions innovantes dans un environnement international collaboratif.',
  'hero.viewExperience': 'Voir l\'Expérience',
  'hero.seeProjects': 'Voir les Projets',
  
  // Experience section
  'experience.title': 'Expérience Professionnelle',
  'experience.bi.title': 'Stagiaire en Business Intelligence',
  'experience.bi.company': 'LPEE',
  'experience.bi.period': 'Février - Juillet 2024',
  'experience.bi.location': 'Maroc',
  'experience.bi.description.1': 'Analysé les besoins métier et identifié les indicateurs clés à surveiller',
  'experience.bi.description.2': 'Nettoyé et transformé les données Excel avec Power Query et Python',
  'experience.bi.description.3': 'Créé des tableaux de bord interactifs dans Power BI pour visualiser les écarts budgétaires',
  'experience.bi.description.4': 'Automatisé les mises à jour des rapports, réduisant le temps de traitement de plusieurs heures à quelques minutes',
  
  'experience.pickone.title': 'Développeur FullStack Mobile',
  'experience.pickone.company': 'Startup "PickOne"',
  'experience.pickone.period': 'Avril 2023 - en cours',
  'experience.pickone.location': 'France',
  'experience.pickone.description.1': 'Développé une application mobile de réseau social basée sur les sondages avec React Native (Expo) et Express.js',
  'experience.pickone.description.2': 'Conçu l\'interface utilisateur avec Figma pour une expérience mobile intuitive et cohérente',
  'experience.pickone.description.3': 'Développé l\'interface mobile avec React Native, Expo et TypeScript',
  'experience.pickone.description.4': 'Intégré des API RESTful via Axios pour l\'authentification, les votes et la gestion des utilisateurs',
  'experience.pickone.description.5': 'Implémenté des fonctionnalités avancées comme le deep linking, le partage de contenu et la gestion des thèmes',
  
  'experience.freelance.title': 'Développeur Mobile',
  'experience.freelance.company': 'Freelance',
  'experience.freelance.period': 'Septembre - Février 2023',
  'experience.freelance.location': 'Maroc',
  'experience.freelance.description.1': 'Développé une application mobile full stack avec React Native, Expo et Firebase',
  'experience.freelance.description.2': 'Recueilli et analysé les attentes des clients pour définir les exigences du projet',
  'experience.freelance.description.3': 'Conçu des interfaces utilisateur intuitives avec Figma',
  'experience.freelance.description.4': 'Développé le frontend et le backend avec des composants réutilisables et l\'intégration Firebase',
  
  // Projects section
  'projects.title': 'Projets',
  'projects.recommendation.title': 'Système de Recommandation pour Plateformes E-learning',
  'projects.recommendation.description': 'Projet de fin d\'études implémentant un système de recommandation sophistiqué utilisant des algorithmes d\'apprentissage automatique pour améliorer l\'expérience d\'apprentissage.',
  'projects.moncomptable.title': 'MonComptable',
  'projects.moncomptable.description': 'Application mobile pour la gestion de stock de supermarché, avec suivi en temps réel et analyse.',
  'projects.stadioapp.title': 'StadioApp',
  'projects.stadioapp.description': 'Application mobile pour la gestion de réservation de terrain de football, avec suivi en temps réel et gestion des réservations.',
  'projects.university.title': 'Système de Gestion de l\'Université',
  'projects.university.description': 'Application web pour la gestion de l\'université, avec gestion des étudiants et des cours.',
  

  // Skills section
  'skills.title': 'Compétences',
  'skills.languages': 'Langues',
  'skills.french': 'Français',
  'skills.english': 'Anglais',
  'skills.arabic': 'Arabe',
  'skills.fluent': 'Courant',
  'skills.professional': 'Professionnel',
  'skills.native': 'Langue Maternelle',
  'skills.categories.frameworks': 'Frameworks',
  'skills.categories.libraries': 'Bibliothèques',
  'skills.categories.programming': 'Langages de Programmation',
  'skills.categories.theoretical': 'Connaissances Théoriques',
  'skills.categories.environments': 'Environnements et IDE',
  'skills.categories.office': 'Compétences Bureautiques',
  
  // Education section
  'education.title': 'Formation',
  'education.mst.degree': 'Master en Sciences et Technologies (MST)',
  'education.mst.school': 'Faculté des Sciences et Techniques - Université Sultan Moulay Slimane',
  'education.mst.period': '2022 - en cours',
  'education.mst.field': 'Intelligence Artificielle & Informatique Numérique (AIDC)',
  'education.mst.status': 'Diplôme prévu en juillet 2025',
  
  'education.lst.degree': 'Licence en Sciences et Techniques (LST)',
  'education.lst.school': 'Faculté des Sciences et Techniques - Université Sultan Moulay Slimane',
  'education.lst.period': '2021 - 2022',
  'education.lst.field': 'Informatique et génie logiciel',
  
  // Interests section
  'interests.title': 'Centres d\'Intérêt',
  'interests.reading.name': 'Lire et écrire des romans fantastiques',
  'interests.reading.description': 'Explorer des mondes imaginaires et créer de nouvelles histoires',
  'interests.manga.name': 'Anime et Manga',
  'interests.manga.description': 'Apprécier les animes et les bandes dessinées japonaises et leur narration unique',
  'interests.science.name': 'Science et Technologie',
  'interests.science.description': 'Explorer les découvertes scientifiques et la recherche surtout dans le domaine de l\'intelligence artificielle',
  'interests.kickboxing.name': 'KickBoxing',
  'interests.kickboxing.description': 'S\'entraîner aux arts martiaux pour la forme et la discipline',
  'interests.swimming.name': 'Natation',
  'interests.swimming.description': 'Rester actif et profiter des sports nautiques',
  'interests.gaming.name': 'Jeux Vidéo (RPG)',
  'interests.gaming.description': 'Explorer des mondes virtuels et les jeux de rôle',
  
  // Contact section
  'contact.title': 'Me Contacter',
  'contact.getInTouch': 'Entrer en Contact',
  'contact.name': 'Nom',
  'contact.email': 'Email',
  'contact.message': 'Message',
  'contact.sendMessage': 'Envoyer le Message',
  'contact.sending': 'Envoi en cours...',
  'contact.success': 'Votre message a été envoyé avec succès !',
  'contact.successSubtext': 'Je vous répondrai dès que possible.',
  'contact.error': 'Une erreur s\'est produite. Veuillez réessayer plus tard.',
  'contact.failed': 'Échec de l\'envoi du message. Veuillez réessayer.',
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('en');

  useEffect(() => {
    // Check for saved language preference or use browser language
    const savedLanguage = localStorage.getItem('language') as Language;
    const browserLanguage = navigator.language.split('-')[0] as Language;
    const initialLanguage = savedLanguage || (browserLanguage === 'fr' ? 'fr' : 'en');
    
    setLanguage(initialLanguage);
  }, []);

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    const translations = language === 'en' ? enTranslations : frTranslations;
    return translations[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 